import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { ChangeEvent, FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  HardwareType,
  MaterialGroupType,
  MaterialPropsType,
  MaterialType,
} from '../../api/appAPI'
import emptyMaterial from '../../assets/emptyMaterial.png'
import { saveGridStateInLocalStorage } from '../../common/helpers'
import {
  addMaterial,
  copyMaterial,
  deleteMaterial,
  editMaterial,
  uploadTexture,
} from '../../redux/itemReducer'
import { StateType } from '../../redux/store'
import {
  EnqueueSnackbarType,
  NearTooltip,
  showMessage,
  UploadButton,
} from '../common/Helpers'
import { actions as appActions } from './../../redux/appReducer'
import DefineMaterial from './DefineMaterial'
import EditMaterial from './EditMaterial'
import useUnit from '../../hooks/useUnit'
import { cmToIn } from '../common/UnitComponents'
import { ConfigsType } from '../../config'

interface IProps {
  materials: MaterialType[]
  hardwaresList: HardwareType[]
  materialGroups: MaterialGroupType[]
  materialGridVisibilityModel: GridColumnVisibilityModel | undefined
  materialGridSortModel: GridSortModel | undefined
  materialGridFilterModel: GridFilterModel | undefined
  activeMaterialId: number
  textures: string[]
  configs: ConfigsType

  addMaterial(
    materialProps: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  editMaterial(
    id: number,
    material: MaterialPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  uploadTexture(
    name: string,
    newTexture: string,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  deleteMaterial(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  copyMaterial(id: number, enqueueSnackbar: EnqueueSnackbarType): void
  setMaterialGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setMaterialGridSortModel(model: GridSortModel | undefined): void
  setMaterialGridFilterModel(model: GridFilterModel | undefined): void
  setActiveMaterialId(value: number): void
  openMaterialGroupDetails(id: number): void
  openHardwareDetails(id: number): void
}

const MaterialTab: FC<IProps> = (props) => {
  const [activeMaterial, setActiveMaterial] = useState<
    undefined | MaterialType
  >(props.materials.find((m) => m.id === props.activeMaterialId))
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteID, setDeleteID] = useState<null | number>(null)
  const [ifSureCheckbox, setIfSureCheckbox] = useState(false)

  const [uploadTextureDialogOpen, setUploadTextureDialogOpen] = useState(false)
  const [uploadTextureName, setUploadTextureName] = useState('')
  const [textureUrl, setTextureUrl] = useState(emptyMaterial)

  const { enqueueSnackbar } = useSnackbar()
  const { unit } = useUnit()

  const handleCopyMaterialClick = (id: number) => {
    props.copyMaterial(id, enqueueSnackbar)
  }

  const handleDeleteMaterialClick = (id: number) => {
    setDeleteID(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteDialogClose = () => {
    setDeleteID(null)
    setDeleteDialogOpen(false)
    setIfSureCheckbox(false)
  }

  const handleDeleteDialogDeleteClick = () => {
    if (deleteID) props.deleteMaterial(deleteID, enqueueSnackbar)
    handleDeleteDialogClose()
  }

  useEffect(() => {
    setActiveMaterial(
      props.materials.find((m) => m.id === props.activeMaterialId)
    )
  }, [props.activeMaterialId])

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.materialGridVisibilityModel,
        filterModel: props.materialGridFilterModel,
        sortModel: props.materialGridSortModel,
      },
      'material'
    )
  }, [
    props.materialGridVisibilityModel,
    props.materialGridFilterModel,
    props.materialGridSortModel,
  ])

  const onTextureChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      var file = e.target.files[0]
      var reader = new FileReader()
      reader.onload = (e) => {
        var image = new Image()
        image.onload = function () {
          // Resize the image
          var canvas = document.createElement('canvas'),
            max_size = 1024, // TODO : pull max size from a site config
            width = image.width,
            height = image.height
          if (width > height) {
            if (width > max_size) {
              height *= max_size / width
              width = max_size
            }
          } else {
            if (height > max_size) {
              width *= max_size / height
              height = max_size
            }
          }

          canvas.width = width
          canvas.height = height

          canvas?.getContext('2d')?.drawImage(image, 0, 0, width, height)

          var dataUrl = canvas.toDataURL('image/jpeg')

          setTextureUrl(dataUrl as string)
          setUploadTextureName(file.name)
        }
        image.src = e.target?.result as string
      }

      if (file) reader.readAsDataURL(file)
    }
  }

  return (
    <Box display="flex" style={{ height: window.innerHeight - 48 }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        pt={2}
        style={{ borderRight: 'solid 1px black', width: '50%' }}
      >
        <Typography variant="h6">Materials List</Typography>

        <Paper style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
          <List component="nav" style={{ padding: 0 }}>
            <ListItem
              button
              onClick={() => {
                props.setActiveMaterialId(0)
              }}
            >
              <ListItemText primary="New Material" />
              <AddIcon />
            </ListItem>
            <Divider />
            <ListItem
              button
              onClick={() => {
                setUploadTextureDialogOpen(true)
              }}
            >
              <ListItemText primary="New Texture" />
              <AddIcon />
            </ListItem>
            {/* {
                            props.materials.map(m => <Fragment key={m.name}>
                                <ListItem button onClick={() => { setActiveMaterial(m) }}>
                                    <Box display='flex'>
                                        <Box style={{ width: 20 }} display='flex' justifyContent='center' mr={1}>
                                            <Typography>{m.type[0].toUpperCase()}</Typography>
                                        </Box>
                                        <Typography>{m.name}</Typography>
                                    </Box>
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => { handleCopyMaterialClick(m.id) }}>
                                            <CopyIcon />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => { handleDeleteMaterialClick(m.id) }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </Fragment>)
                        } */}
          </List>
        </Paper>
        <DataGrid
          style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}
          columns={[
            { field: 'id' },
            {
              field: 'date',
              headerName: 'Last Modified',
              type: 'datetime',
              minWidth: 160,
            },
            { field: 'name', headerName: 'Name', minWidth: 200 },
            { field: 'type', headerName: 'Type', width: 110 },
            { field: 'cab', headerName: 'Cab', width: 110 },
            { field: 'door', headerName: 'Door', width: 110 },
            { field: 'price', headerName: 'Price', width: 110 },
            { field: 'addon', headerName: 'Addon', width: 120 },
            { field: 'texture', headerName: 'Texture Url', width: 200 },
            {
              field: 'length',
              headerName: 'Length',
              width: 120,
              description: 'Texture Length',
            },
            {
              field: 'width',
              headerName: 'Width',
              width: 120,
              description: 'Texture Width',
            },
            { field: 'retail', headerName: 'Retail', width: 110 },
            { field: 'dealer', headerName: 'Dealer', width: 110 },
            { field: 'designer', headerName: 'Designer', width: 110 },
            {
              field: 'actions',
              headerName: 'Actions',
              filterable: false,
              sortable: false,
              renderCell: (params: GridCellParams) => (
                <Box
                  onClick={(e) => {
                    e.stopPropagation() // don't select this row after clicking
                  }}
                >
                  <IconButton
                    edge="end"
                    onClick={() => {
                      handleCopyMaterialClick(parseInt(params.id.toString()))
                    }}
                  >
                    <CopyIcon />
                  </IconButton>
                  {props.materials.find(
                    (m) => m.id === parseInt(params.id.toString())
                  )?.editable === '1' && (
                    <IconButton
                      edge="end"
                      onClick={() => {
                        handleDeleteMaterialClick(
                          parseInt(params.id.toString())
                        )
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ),
              width: 150,
            },
          ]}
          // hideFooterPagination
          rows={props.materials.map((material) => ({
            id: material.id,
            date: new Date(material.date),
            name: material.name,
            type: material.type[0].toUpperCase(),
            cab: material.cabinetDoor[0] === '1' ? '✓' : '',
            door: material.cabinetDoor[1] === '1' ? '✓' : '',
            price: material.price,
            addon: material.addon,
            texture: material.texture.url,
            length: unit === 'cm' ? material.length : cmToIn(material.length),
            width: unit === 'cm' ? material.width : cmToIn(material.width),
            retail: material.permissions[0] === '1' ? '✓' : '',
            dealer: material.permissions[1] === '1' ? '✓' : '',
            designer: material.permissions[2] === '1' ? '✓' : '',
          }))}
          onRowClick={(param) => {
            props.setActiveMaterialId(parseInt(param.id.toString()))
          }}
          columnVisibilityModel={props.materialGridVisibilityModel}
          onColumnVisibilityModelChange={(model) => {
            props.setMaterialGridVisibilityModel(model)
          }}
          sortModel={props.materialGridSortModel}
          onSortModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.materialGridSortModel)
            )
              props.setMaterialGridSortModel(model)
          }}
          filterModel={props.materialGridFilterModel}
          onFilterModelChange={(model) => {
            if (
              JSON.stringify(model) !==
              JSON.stringify(props.materialGridFilterModel)
            )
              props.setMaterialGridFilterModel(model)
          }}
          rowSelectionModel={props.activeMaterialId}
        />
      </Box>
      {activeMaterial === undefined ? (
        <DefineMaterial
          textures={props.textures}
          distributorCompanies={props.configs.floorDistributorCompanies.strValues.split(
            ','
          )}
          addMaterial={props.addMaterial}
        />
      ) : (
        <EditMaterial
          material={activeMaterial}
          textures={props.textures}
          distributorCompanies={props.configs.floorDistributorCompanies.strValues.split(
            ','
          )}
          editMaterial={props.editMaterial}
        />
      )}

      <Dialog open={deleteDialogOpen}>
        <DialogTitle>Delete Material</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete{' '}
            <b>
              "
              {
                props.materials.find((material) => material.id === deleteID)
                  ?.name
              }
              "
            </b>{' '}
            material?
          </Typography>
          <br />
          {(props.hardwaresList.filter((h) =>
            h.materialsIds.includes(deleteID ? deleteID.toString() : '0')
          ).length > 0 ||
            props.materialGroups.filter((mg) =>
              mg.materialsIds.includes(deleteID ? deleteID : 0)
            ).length > 0) && (
            <>
              <Typography>Material is attached to</Typography>
              {props.hardwaresList
                .filter((h) =>
                  h.materialsIds.includes(deleteID ? deleteID.toString() : '0')
                )
                .map((h) => (
                  <NearTooltip
                    title="Click to open hardware details"
                    placement="top"
                  >
                    <Typography
                      key={h.id}
                      component="li"
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                      onClick={() => {
                        props.openHardwareDetails(h.id)
                      }}
                    >
                      {h.name} - hardware
                    </Typography>
                  </NearTooltip>
                ))}
              {props.materialGroups
                .filter((mg) =>
                  mg.materialsIds.includes(deleteID ? deleteID : 0)
                )
                .map((mg) => (
                  <NearTooltip
                    title="Click to open material group details"
                    placement="top"
                  >
                    <Typography
                      key={mg.id}
                      component="li"
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                      onClick={() => {
                        props.openMaterialGroupDetails(mg.id)
                      }}
                    >
                      {mg.name} - material group
                    </Typography>
                  </NearTooltip>
                ))}
              <br />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ifSureCheckbox}
                    onChange={(e) => {
                      setIfSureCheckbox(e.target.checked)
                    }}
                  />
                }
                label="Yes, I'm sure!"
              />
            </>
          )}
          {props.hardwaresList.filter(
            (h) => h.defaultMaterialId === (deleteID ? deleteID : 0)
          ).length > 0 && (
            <>
              <br />
              <Typography>Material is default material of </Typography>
              {props.hardwaresList
                .filter(
                  (h) => h.defaultMaterialId === (deleteID ? deleteID : 0)
                )
                .map((h) => (
                  <NearTooltip
                    title="Click to open hardware details"
                    placement="top"
                  >
                    <Typography
                      key={h.id}
                      component="li"
                      style={{ cursor: 'pointer', width: 'fit-content' }}
                      onClick={() => {
                        props.openHardwareDetails(h.id)
                      }}
                    >
                      {h.name}
                    </Typography>
                  </NearTooltip>
                ))}
              <br />
              <Typography>
                Change default materials of hardwares to delete material
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteDialogDeleteClick}
            color="secondary"
            disabled={(() => {
              const isDefMaterial =
                props.hardwaresList.filter(
                  (h) => h.defaultMaterialId === (deleteID ? deleteID : 0)
                ).length > 0
              const isAttachedAndSure =
                props.hardwaresList.filter((h) =>
                  h.materialsIds.includes(deleteID ? deleteID.toString() : '0')
                ).length > 0 ||
                props.materialGroups.filter((mg) =>
                  mg.materialsIds.includes(deleteID ? deleteID : 0)
                ).length > 0
                  ? !ifSureCheckbox
                  : false

              const isDisabled = isDefMaterial || isAttachedAndSure
              return isDisabled
            })()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
        open={uploadTextureDialogOpen}
      >
        <DialogTitle>Upload Texture</DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <img src={textureUrl} width={50} height={50} alt="texture" />
          <TextField
            label="Texture Name"
            variant="outlined"
            value={uploadTextureName}
            onChange={(e) => {
              setUploadTextureName(e.target.value)
            }}
            style={{ width: '60%' }}
          />
          <UploadButton buttonText="Browse" onFileChange={onTextureChange} />
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => {
              setUploadTextureName('')
              setTextureUrl('')
              setUploadTextureDialogOpen(false)
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (props.textures.includes(uploadTextureName)) {
                showMessage(
                  enqueueSnackbar,
                  'texture with name ' + uploadTextureName + ' already exists'
                )
              } else {
                props.uploadTexture(
                  uploadTextureName,
                  textureUrl,
                  enqueueSnackbar
                )
                setUploadTextureName('')
                setTextureUrl('')
                setUploadTextureDialogOpen(false)
              }
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const mapStateToProps = (state: StateType) => ({
  materials: state.itemReducer.materials,
  textures: state.itemReducer.textures,
  materialGroups: state.itemReducer.materialGroups,
  hardwaresList: state.itemReducer.hardwaresList,
  materialGridVisibilityModel: state.appReducer.materialGrid.visibilityModel,
  materialGridSortModel: state.appReducer.materialGrid.sortModel,
  materialGridFilterModel: state.appReducer.materialGrid.filterModel,
  activeMaterialId: state.appReducer.activeMaterialId,
  configs: state.appReducer.configs,
})

export default connect(mapStateToProps, {
  addMaterial,
  editMaterial,
  uploadTexture,
  deleteMaterial,
  copyMaterial,
  setMaterialGridVisibilityModel: appActions.setMaterialGridVisibilityModel,
  setMaterialGridSortModel: appActions.setMaterialGridSortModel,
  setMaterialGridFilterModel: appActions.setMaterialGridFilterModel,
  setActiveMaterialId: appActions.setActiveMaterialId,
  openHardwareDetails: appActions.openHardwareDetails,
  openMaterialGroupDetails: appActions.openMaterialGroupDetails,
})(MaterialTab)
